// This file was created using Claude AI on 2026-01-06
import { UserText } from '@Constants/UserText';
import * as Sentry from '@sentry/react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

export const RouterErrorBoundary: FC = () => {
    const error = useRouteError();
    const [showDetails, setShowDetails] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [sentryEventId, setSentryEventId] = useState<string | undefined>(undefined);

    useEffect(() => {
        const eventId = Sentry.captureException(error);
        setSentryEventId(eventId);
    }, [error]);

    const errorInfo = useMemo(() => {
        if (isRouteErrorResponse(error)) {
            return {
                title: UserText.ERROR_BOUNDARY_TITLE,
                message: `${UserText.ERROR_BOUNDARY_MESSAGE} (Status: ${error.status})`,
                details: error.statusText || error.data?.message || 'Keine Details verfügbar',
                stack: undefined,
            };
        }

        if (error instanceof Error) {
            return {
                title: UserText.ERROR_BOUNDARY_TITLE,
                message: UserText.ERROR_BOUNDARY_MESSAGE,
                details: error.message,
                stack: error.stack,
            };
        }

        return {
            title: UserText.ERROR_BOUNDARY_TITLE,
            message: UserText.ERROR_BOUNDARY_MESSAGE,
            details: String(error),
            stack: undefined,
        };
    }, [error]);

    const traceId = useMemo(() => {
        return sentryEventId ? sentryEventId : UserText.ERROR_TRACE_ID_UNAVAILABLE;
    }, [sentryEventId]);

    const handleReload = useCallback(() => {
        window.location.reload();
    }, []);

    const handleGoHome = useCallback(() => {
        window.location.href = '/';
    }, []);

    const handleCopyTraceId = useCallback(async () => {
        if (traceId !== UserText.ERROR_TRACE_ID_UNAVAILABLE) {
            try {
                await navigator.clipboard.writeText(traceId);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            } catch (err) {
                console.error('Failed to copy trace ID:', err);
            }
        }
    }, [traceId]);

    return (
        <div className="fixed inset-0 bg-(--mantine-color-black)/50 flex items-center justify-center z-[9999] p-5 font-sans">
            <div className="max-w-2xl w-full bg-(--mantine-color-white) rounded-lg p-6 shadow-lg">
                <div className="flex flex-col gap-4">
                    <h1 className="m-0 text-2xl text-(--mantine-color-red-7)">{errorInfo.title}</h1>
                    <p className="m-0 text-(--mantine-color-gray-9)">{errorInfo.message}</p>

                    <div className="p-3 bg-(--mantine-color-gray-1) rounded border border-(--mantine-color-gray-4)">
                        <div className="flex items-center gap-2">
                            <strong>{UserText.ERROR_TRACE_ID_LABEL}:</strong>
                            <code className="flex-1">{traceId}</code>
                            {traceId !== UserText.ERROR_TRACE_ID_UNAVAILABLE && (
                                <button
                                    onClick={handleCopyTraceId}
                                    className="px-3 py-1 bg-(--mantine-color-primary-filled) text-(--mantine-primary-color-contrast) border-none rounded cursor-pointer text-sm hover:bg-(--mantine-color-primary-filled-hover)"
                                >
                                    {copySuccess ? UserText.ERROR_TRACE_ID_COPIED : UserText.ERROR_TRACE_ID_COPY}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={handleReload}
                            className="px-5 py-2.5 bg-(--mantine-color-primary-filled) text-(--mantine-primary-color-contrast) border-none rounded cursor-pointer text-base font-medium hover:bg-(--mantine-color-primary-filled-hover)"
                        >
                            {UserText.ERROR_RELOAD_BUTTON}
                        </button>
                        <button
                            onClick={handleGoHome}
                            className="px-5 py-2.5 bg-(--mantine-color-gray-7) text-(--mantine-color-white) border-none rounded cursor-pointer text-base font-medium hover:bg-(--mantine-color-gray-8)"
                        >
                            {UserText.ERROR_HOME_BUTTON}
                        </button>
                    </div>

                    <div>
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="px-4 py-2 bg-(--mantine-color-gray-1) text-(--mantine-color-link-text) border border-(--mantine-color-gray-4) rounded cursor-pointer text-sm font-medium hover:bg-(--mantine-color-gray-2)"
                        >
                            {showDetails ? '▼' : '▶'} {UserText.ERROR_TECHNICAL_DETAILS}
                        </button>

                        {showDetails && (
                            <div className="mt-3 p-3 bg-(--mantine-color-gray-1) rounded border border-(--mantine-color-gray-4) max-h-96 overflow-y-auto overflow-x-auto">
                                <pre className="m-0 text-xs whitespace-pre-wrap text-(--mantine-color-gray-9)">
                                    {errorInfo.details}
                                    {errorInfo.stack && `\n\nStack Trace:\n${errorInfo.stack}`}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
